import type { ReactNode } from "react";

/**
 * One editable field in a row.
 *
 * `get`/`set` rather than a plain key because the DTOs nest — an account's
 * email lives at `credentials.email`, and a bundle's quantity two levels down.
 * `set` returns a new row rather than mutating, so cancelling an edit is just
 * throwing the draft away.
 */
export type EditorField<T> = EditorFieldKind<T> & {
  /** Shown, not edited, on a row it says so: a value the server works out for that row. */
  readOnly?: (row: T) => boolean;
};

type EditorFieldKind<T> =
  | {
      kind: "text" | "number" | "email";
      get: (row: T) => string | number | null | undefined;
      set: (row: T, value: string) => T;
      required?: boolean;
      placeholder?: string;
    }
  | {
      kind: "select";
      get: (row: T) => string | null | undefined;
      set: (row: T, value: string) => T;
      /**
       * A function when the choices depend on the row being edited — a
       * province list narrowed by the country picked one cell over.
       */
      options:
        | Array<{ value: string; label: string }>
        | ((row: T) => Array<{ value: string; label: string }>);
      required?: boolean;
    }
  | {
      kind: "checkbox";
      get: (row: T) => boolean;
      set: (row: T, value: boolean) => T;
    }
  | {
      /**
       * Several reference rows own a set rather than a value — a production
       * centre's countries, a client's feed types. Vaadin used a
       * `MultiSelectComboBox` in the editor cell; this is the same idea.
       */
      kind: "multiselect";
      get: (row: T) => string[];
      set: (row: T, value: string[]) => T;
      options: Array<{ value: string; label: string }>;
      required?: boolean;
    };

export interface RowEditorConfig<T> {
  /** Column id → field descriptor. Columns absent here stay read-only. */
  fields: Record<string, EditorField<T>>;
  /** A blank entity for the add row. Omit to disable adding. */
  newRow?: () => T;
  /**
   * Adjusts a row on its way into the edit draft.
   *
   * For write-only fields, which cannot be handled in the field's `get`. Editor
   * inputs are controlled by `get(draft)`, so a getter that returns something
   * other than what `set` just wrote makes the cell unfillable — every
   * keystroke is written to the draft and then read back as the old value. A
   * secret that must not be displayed therefore has to be cleared here, once,
   * before editing starts.
   */
  toDraft?: (row: T) => T;
  /** Persist. Throw to keep the row in edit mode with the error shown. */
  onSave: (row: T, isNew: boolean) => Promise<void> | void;
  /** Omit to disable deleting. */
  onDelete?: (row: T) => Promise<void> | void;
  /** Field-level messages keyed by column id; a non-empty result blocks save. */
  validate?: (row: T, isNew: boolean) => Record<string, string> | null;
  /** Confirmation copy for deletes. */
  deleteConfirm?: (row: T) => { title: string; message?: string };
  /** Extra per-row controls beside Edit/Delete. */
  rowActions?: (row: T) => ReactNode;
  addLabel?: string;
}

export interface EditingState<T> {
  /** Row id being edited, or `__new__` for the add row. */
  id: string;
  draft: T;
  isNew: boolean;
  errors: Record<string, string>;
  saving: boolean;
}

export const NEW_ROW_ID = "__new__";
